import { Button, ButtonProps } from '@hopin-team/ui-button';
import { Flex } from '@hopin-team/ui-flex';
import { getFromTheme } from '@hopin-team/ui-helper-deep-branding';
import { Icon, IconProp } from '@hopin-team/ui-icon';
import React, { ReactNode } from 'react';
import { useLocation, useNavigate, useResolvedPath } from 'react-router-dom';
import styled from 'styled-components';

import { MenuItem } from './menu-items';

export type NavButtonProps = {
  icon?: IconProp;
  customIcon?: ReactNode;
  label: string;
  link?: string;
  withSpacing?: boolean;
  onClick?: (args: { label: string }) => void;
  openInNewTab?: boolean;
};

const isActivePath = (
  activePath: string | string[],
  locationPathname: string,
) => {
  if (Array.isArray(activePath)) {
    return activePath.some(path => locationPathname.startsWith(path));
  }
  return locationPathname.startsWith(activePath);
};

const NavButtonComponent = ({
  icon: iconProp,
  label,
  onClick,
  customIcon,
  link,
  withSpacing = false,
  openInNewTab = false,
  iconSuffix,
  activePath,
  ...rest
}: NavButtonProps &
  ButtonProps &
  Pick<MenuItem, 'iconSuffix' | 'activePath'>) => {
  const navigate = useNavigate();
  const { pathname: locationPathname } = useLocation();
  const { pathname: toPathname } = useResolvedPath(link ?? '/');

  if (link) {
    if (
      locationPathname.replace(/\/$/, '').toLowerCase() ===
        toPathname.toLowerCase() ||
      (locationPathname === '/' && toPathname === '/') ||
      (activePath && isActivePath(activePath, locationPathname))
    ) {
      rest.className = `${rest?.className} active`;
    }
  }

  if (openInNewTab) {
    rest.target = '_blank';
    rest.rel = 'noopener';
  }

  return (
    <Button
      pattern="primary"
      isGhost
      leadingIcon={iconProp}
      size="small"
      isInline
      pl={2}
      ml={withSpacing ? 'auto' : undefined}
      pr={3}
      onClick={() => {
        if (onClick) onClick({ label: label });
        if (link) navigate(link);
      }}
      {...rest}
    >
      {customIcon ? <Flex mr={1}>{customIcon}</Flex> : null}
      {label}
      {iconSuffix ? <Flex ml={1}>{iconSuffix}</Flex> : null}
    </Button>
  );
};

export const NavButton = styled(NavButtonComponent)`
  color: ${({ theme }) => getFromTheme(`COLOR.GREY['700']`, theme)};
  height: auto;
  border-radius: 0;

  > div:last-child ${Icon} {
    width: 0.5rem;
    height: 0.5rem;
  }

  &:hover,
  &:focus {
    background-color: ${({ theme }) =>
      getFromTheme(`COLOR.BLUE['100']`, theme)};
    border-radius: 0;
    color: ${({ theme }) => getFromTheme(`COLOR.GREY['900']`, theme)};

    &:before {
      display: none;
    }
  }

  &.active {
    color: ${({ theme }) => getFromTheme(`COLOR.GREY['900']`, theme)};
    border-bottom: 2px solid
      ${({ theme }) => getFromTheme(`COLOR.BLUE['500']`, theme)};

    svg {
      color: ${({ theme }) => getFromTheme(`COLOR.BLUE['500']`, theme)};
    }
  }

  svg {
    color: ${({ theme }) => getFromTheme(`COLOR.GREY['500']`, theme)};
  }
`;

export const MenuNavButton = styled(NavButton)`
  height: 100%;

  &.open div:first-child .hpn-Icon svg {
    color: ${({ theme }) => getFromTheme(`COLOR.BLUE['500']`, theme)};
  }
`;
